import { sessionDateOnly, sessionTimeHms } from "./session-timing.js";

export const BILLING_STATEMENT_POSITION_STEP = 10;

function nz(v) {
  return String(v ?? "").trim();
}

/**
 * @param {number} index nullbasierter Index in der Positionsliste
 */
export function billingStatementPositionNumber(index) {
  const i = Number(index);
  if (!Number.isFinite(i) || i < 0) return BILLING_STATEMENT_POSITION_STEP;
  return (Math.floor(i) + 1) * BILLING_STATEMENT_POSITION_STEP;
}

/**
 * @param {number} positionNumber
 * @param {(k: string, p?: object) => string} t
 */
export function formatBillingStatementPositionLabel(positionNumber, t) {
  const safeT = typeof t === "function" ? t : (k) => k;
  const n = Number(positionNumber);
  if (!Number.isFinite(n) || n <= 0)
    return safeT("trainerstunden.planning.fieldDash");
  return safeT("trainerstunden.billingStatements.item.positionLabel", {
    number: n,
  });
}

/**
 * @param {object | null | undefined} item
 * @param {object[] | null | undefined} sessions
 * @returns {object | null}
 */
export function resolveBillingStatementItemSession(item, sessions) {
  const sessionId = nz(item?.session_id);
  if (!sessionId || (nz(item?.kind) && nz(item?.kind) !== "session"))
    return null;
  const list = Array.isArray(sessions) ? sessions : [];
  return list.find((s) => nz(s?.id) === sessionId) || null;
}

/**
 * @param {object | null | undefined} session
 * @param {(k: string, p?: object) => string} t
 */
export function resolveBillingStatementTrainingTypeLabel(session, t) {
  const safeT = typeof t === "function" ? t : (k) => k;
  const type = nz(session?.training_type);
  if (type === "single") return safeT("trainerstunden.training.single");
  if (type === "pair") return safeT("trainerstunden.training.pair2");
  if (type === "group") return safeT("trainerstunden.training.group");
  return safeT("trainerstunden.planning.fieldDash");
}

/**
 * @param {object | null | undefined} session
 * @param {(k: string, p?: object) => string} t
 */
export function formatBillingStatementSessionTimeRange(session, t) {
  const safeT = typeof t === "function" ? t : (k) => k;
  const date = sessionDateOnly(session?.date);
  const hms = sessionTimeHms(session?.start_time);
  if (!date || !hms) return safeT("trainerstunden.planning.fieldDash");
  try {
    const start = new Date(`${date}T${hms}`);
    if (!Number.isFinite(start.getTime()))
      return safeT("trainerstunden.planning.fieldDash");
    const dur = Number(session?.duration_min) || 60;
    const end = new Date(
      start.getTime() + (Number.isFinite(dur) && dur > 0 ? dur : 60) * 60000,
    );
    const tf = new Intl.DateTimeFormat("de-DE", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    return `${tf.format(start)} - ${tf.format(end)}`;
  } catch {
    return safeT("trainerstunden.planning.fieldDash");
  }
}

/**
 * @param {string | null | undefined} description
 */
export function isLegacyBillingStatementItemDescription(description) {
  const d = nz(description);
  if (!d) return false;
  return /·\s*Anteil\s/i.test(d) || /€\/h/i.test(d);
}

/**
 * Unaufgelöster i18n-Key (z. B. "trainerstunden.billingStatements.item.sessionLine")
 * darf niemals als Leistungstext erscheinen.
 * @param {string | null | undefined} value
 */
export function looksLikeBillingI18nKey(value) {
  const v = nz(value);
  if (!v || /\s/.test(v)) return false;
  return /^[A-Za-z0-9_-]+(?:\.[A-Za-z0-9_-]+)+$/.test(v);
}

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

/**
 * Platzkosten-Anteil einer Rechnungsposition (pricing_snapshot oder Direktfeld).
 * @param {object | null | undefined} item
 */
export function resolveBillingItemCourtFeeShareEur(item) {
  const snap =
    item?.pricing_snapshot && typeof item.pricing_snapshot === "object"
      ? item.pricing_snapshot
      : null;
  if (snap) {
    const fromSnap = Number(snap.court_fee_share_eur ?? snap.courtFeeShareEur);
    if (Number.isFinite(fromSnap) && fromSnap > 0.004) return round2(fromSnap);
  }
  const direct = Number(item?.courtFeeShareEur ?? item?.court_fee_share_eur);
  if (Number.isFinite(direct) && direct > 0.004) return round2(direct);
  return 0;
}

/**
 * Gastspielergebühr einer Rechnungsposition (pricing_snapshot oder Direktfeld).
 * @param {object | null | undefined} item
 */
export function resolveBillingItemGuestFeeAppliedEur(item) {
  const snap =
    item?.pricing_snapshot && typeof item.pricing_snapshot === "object"
      ? item.pricing_snapshot
      : null;
  if (snap) {
    const fromSnap = Number(
      snap.guest_fee_applied_eur ?? snap.guestFeeAppliedEur,
    );
    if (Number.isFinite(fromSnap) && fromSnap > 0.004) return round2(fromSnap);
  }
  const direct = Number(
    item?.guestFeeAppliedEur ?? item?.guest_fee_applied_eur,
  );
  if (Number.isFinite(direct) && direct > 0.004) return round2(direct);
  return 0;
}

/**
 * @param {number} durationMin
 */
export function formatBillingDurationHoursLabel(durationMin) {
  const mins = Number(durationMin) || 0;
  if (!(mins > 0)) return "1";
  const hours = mins / 60;
  if (Number.isInteger(hours)) return String(hours);
  const rounded = Math.round(hours * 100) / 100;
  return String(rounded).replace(".", ",");
}

/**
 * Eine DB-Zeile (Training + optional Platz) → sichtbare Teilzeilen für Übersicht/Rechnung.
 *
 * @param {{
 *   item?: object | null,
 *   session?: object | null,
 *   t: (k: string, p?: object) => string,
 *   amountEur?: number,
 * }} input
 * @returns {Array<{ kind: 'training'|'court'|'guest'|'session', title: string, amountEur: number }>}
 */
export function expandBillingSessionItemDisplayLines({
  item,
  session,
  t,
  amountEur,
} = {}) {
  const safeItem = item && typeof item === "object" ? item : {};
  const safeT = typeof t === "function" ? t : (k) => k;
  const total = round2(
    Number(
      amountEur ??
        safeItem.amount_eur ??
        safeItem.amountEur ??
        safeItem.shareEur,
    ) || 0,
  );
  const courtShare = resolveBillingItemCourtFeeShareEur(safeItem);
  const guestFee = resolveBillingItemGuestFeeAppliedEur(safeItem);
  const trainingAmt = round2(total - courtShare - guestFee);
  const hasExtras = courtShare > 0.004 || guestFee > 0.004;
  if (!hasExtras || !(trainingAmt > 0.004)) {
    return [
      {
        kind: "session",
        title: formatBillingStatementItemTitle({
          item: safeItem,
          session,
          t: safeT,
        }),
        amountEur: total,
      },
    ];
  }
  const hours = formatBillingDurationHoursLabel(
    resolveBillingStatementItemDurationMin(safeItem, session),
  );
  const lines = [
    {
      kind: "training",
      title: safeT("trainerstunden.billingStatements.item.trainingHoursLine", {
        hours,
      }),
      amountEur: trainingAmt,
    },
  ];
  if (courtShare > 0.004) {
    lines.push({
      kind: "court",
      title: safeT("trainerstunden.billingStatements.item.courtBookingLine", {
        hours,
      }),
      amountEur: courtShare,
    });
  }
  if (guestFee > 0.004) {
    lines.push({
      kind: "guest",
      title: safeT("trainerstunden.billingStatements.item.guestFeeLine"),
      amountEur: guestFee,
    });
  }
  return lines;
}

/**
 * Kompakte Leistungszeile: „Gruppentraining von 17:00 - 18:00 Uhr“.
 *
 * @param {{ item: object, session?: object | null, t: (k: string, p?: object) => string }} input
 */
export function formatBillingStatementItemTitle({ item, session, t }) {
  const safeItem = item && typeof item === "object" ? item : {};
  const safeT = typeof t === "function" ? t : (k) => k;
  const resolvedSession = mergeBillingStatementItemEffectiveSession(
    safeItem,
    session || null,
  );
  const dash = safeT("trainerstunden.planning.fieldDash");

  if (
    resolvedSession &&
    (!nz(safeItem.kind) || nz(safeItem.kind) === "session")
  ) {
    const timeRange = formatBillingStatementSessionTimeRange(
      resolvedSession,
      safeT,
    );
    const training = resolveBillingStatementTrainingTypeLabel(
      resolvedSession,
      safeT,
    );
    if (timeRange !== dash && training !== dash) {
      return safeT("trainerstunden.billingStatements.item.sessionLine", {
        training,
        timeRange,
      });
    }
    if (training !== dash) return training;
    if (timeRange !== dash) return timeRange;
  }

  const snap = nz(safeItem.line_title_snapshot);
  if (
    snap &&
    !isLegacyBillingStatementItemDescription(snap) &&
    !looksLikeBillingI18nKey(snap)
  )
    return snap;

  const desc = nz(safeItem.description);
  if (
    desc &&
    !isLegacyBillingStatementItemDescription(desc) &&
    !looksLikeBillingI18nKey(desc)
  )
    return desc;

  return safeT("trainerstunden.billingInbox.itemTitleFallback");
}

/**
 * Abrechnungs-Dauer: pricing_snapshot hat Vorrang vor Session (Entwurfs-Korrektur).
 *
 * @param {object | null | undefined} item
 * @param {object | null | undefined} session
 */
export function resolveBillingStatementItemDurationMin(item, session) {
  const snap = item?.pricing_snapshot;
  if (snap && typeof snap === "object") {
    const fromSnap = Number(snap.duration_min);
    if (Number.isFinite(fromSnap) && fromSnap > 0) return Math.floor(fromSnap);
  }
  const fromSession = Number(session?.duration_min);
  if (Number.isFinite(fromSession) && fromSession > 0)
    return Math.floor(fromSession);
  return 60;
}

/**
 * Session mit ggf. überschriebener Dauer aus der Position (Rechnungsentwurf).
 *
 * @param {object | null | undefined} item
 * @param {object | null | undefined} session
 */
export function mergeBillingStatementItemEffectiveSession(item, session) {
  if (!session || typeof session !== "object") return session || null;
  const dur = resolveBillingStatementItemDurationMin(item, session);
  if (Number(session.duration_min) === dur) return session;
  return { ...session, duration_min: dur };
}

/**
 * @param {object | null | undefined} person
 * @param {(k: string, p?: object) => string} t
 */
export function formatBillingStatementPersonFirstName(person, t) {
  const safeT = typeof t === "function" ? t : (k) => k;
  const first = nz(person?.first_name);
  if (first) return first;
  const last = nz(person?.last_name);
  if (last) return last;
  return safeT("trainerstunden.planning.fieldDash");
}

/**
 * @param {object | null | undefined} item
 * @param {object | null | undefined} person
 * @param {(k: string, p?: object) => string} t
 */
export function resolveBillingStatementItemPersonFirstName(item, person, t) {
  const snap = nz(item?.person_name_snapshot);
  if (snap) return snap;
  return formatBillingStatementPersonFirstName(person, t);
}

/** @param {string | null | undefined} dateStr */
export function formatBillingStatementItemDateDe(dateStr) {
  if (!dateStr) return "";
  const date = new Date(`${String(dateStr).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(date.getTime())) return String(dateStr).slice(0, 10);
  return date.toLocaleDateString("de-DE");
}

/**
 * @param {object | null | undefined} trainer
 * @param {(k: string, p?: object) => string} t
 */
export function formatBillingStatementTrainerFirstName(trainer, t) {
  const safeT = typeof t === "function" ? t : (k) => k;
  const tr = trainer && typeof trainer === "object" ? trainer : null;
  const first = nz(tr?.first_name);
  if (first) return first;
  const last = nz(tr?.last_name);
  if (last) return last;
  return safeT("trainerstunden.planning.fieldDash");
}

/**
 * @param {{
 *   item: object,
 *   session?: object | null,
 *   trainer?: object | null,
 *   t: (k: string, p?: object) => string,
 *   amountStr: string,
 * }} input
 */
export function formatBillingStatementSendLine({
  item,
  session,
  trainer,
  t,
  amountStr,
}) {
  const safeT = typeof t === "function" ? t : (k) => k;
  const safeItem = item && typeof item === "object" ? item : {};
  const resolvedSession = session || null;
  const dateLabel = formatBillingStatementItemDateDe(safeItem.date);
  const detail = formatBillingStatementItemTitle({
    item: safeItem,
    session: resolvedSession,
    t: safeT,
  });
  const trainerLabel = formatBillingStatementTrainerFirstName(trainer, safeT);

  if (resolvedSession && dateLabel && detail) {
    return safeT("trainerstunden.billingStatements.message.itemLine", {
      date: dateLabel,
      detail,
      trainer: trainerLabel,
      amount: amountStr,
    });
  }

  const fallbackDesc =
    nz(safeItem.description) ||
    safeT("trainerstunden.billingInbox.itemTitleFallback");
  return safeT("trainerstunden.billingStatements.message.itemLineFallback", {
    description: fallbackDesc,
    amount: amountStr,
  });
}
